import { useEffect, useRef, useState } from "react"

import useRequest from "hooks/useRequest"
import {
  getDistributorConfigs,
  putDistributorConfigs,
} from "helpers/backend_helper"

import {
  PROVIDERS_TIME_END,
  PROVIDERS_TIME_START,
  SAVED_FEEDBACK_MS,
  SCALE_END,
  SCALE_START,
  SORT_HORA,
  SORT_NOMBRE,
} from "./static"

const toMinutes = time => {
  if (!time) return null
  const parts = String(time).split(":")
  return (Number(parts[0]) || 0) * 60 + (Number(parts[1]) || 0)
}

export const duracion = (inicio, fin) => {
  const a = toMinutes(inicio)
  const b = toMinutes(fin)
  if (a === null || b === null) return "—"
  const diff = b - a
  if (diff <= 0) return "—"
  const horas = Math.floor(diff / 60)
  const minutos = diff % 60
  return `${horas ? `${horas}h` : ""}${
    minutos ? `${horas ? " " : ""}${minutos}m` : ""
  }`
}

export const esInvalido = cliente => {
  const a = toMinutes(cliente.inicio)
  const b = toMinutes(cliente.fin)
  if (a === null || b === null) return true
  return b <= a
}

export const barPercent = time => {
  const minutos = toMinutes(time)
  if (minutos === null) return 0
  const pct = ((minutos - SCALE_START) / (SCALE_END - SCALE_START)) * 100
  return Math.max(0, Math.min(100, pct))
}

const normalizar = response => {
  const configs = response?.configs || []
  const buscarConfig = name =>
    configs.find(config => config.name === name)?.value || ""

  return {
    prov: {
      start: buscarConfig(PROVIDERS_TIME_START),
      end: buscarConfig(PROVIDERS_TIME_END),
    },
    clientes: (response?.enterprises || [])
      .filter(enterprise => enterprise.isActivated)
      .map(enterprise => ({
        id: enterprise.id,
        nombre: enterprise.company_name || "",
        inicio: enterprise.pivot?.start || "",
        fin: enterprise.pivot?.end || "",
        comision:
          enterprise.fee_per_unit === null ||
          enterprise.fee_per_unit === undefined
            ? ""
            : String(enterprise.fee_per_unit),
      })),
  }
}

const clonar = modelo => ({
  prov: { ...modelo.prov },
  clientes: modelo.clientes.map(cliente => ({ ...cliente })),
})

export default function useConfigs() {
  const { isLoading, req } = useRequest()

  const [modelo, setModelo] = useState(null)
  const [original, setOriginal] = useState(null)

  const [search, setSearch] = useState("")
  const [sort, setSort] = useState(SORT_NOMBRE)
  const [onlyChanged, setOnlyChanged] = useState(false)
  const [selected, setSelected] = useState([])

  const [bulk, setBulk] = useState({ inicio: "", fin: "", comision: "" })

  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const savedTimer = useRef(null)

  useEffect(() => {
    const cargar = async () => {
      try {
        const response = await req(getDistributorConfigs())
        const normalizado = normalizar(response)
        setModelo(normalizado)
        setOriginal(clonar(normalizado))
      } catch (error) {
        setModelo(null)
      }
    }
    cargar()

    return () => clearTimeout(savedTimer.current)
  }, [])

  const clientes = modelo?.clientes || []
  const originalDe = id =>
    (original?.clientes || []).find(cliente => cliente.id === id) || {}

  const estaModificado = cliente => {
    const previo = originalDe(cliente.id)
    return (
      previo.inicio !== cliente.inicio ||
      previo.fin !== cliente.fin ||
      previo.comision !== cliente.comision
    )
  }

  const provModificada =
    !!modelo &&
    !!original &&
    (modelo.prov.start !== original.prov.start ||
      modelo.prov.end !== original.prov.end)

  const clientesModificados = clientes.filter(estaModificado)
  const dirtyCount = clientesModificados.length + (provModificada ? 1 : 0)
  const hayInvalidos = clientesModificados.some(esInvalido)

  const query = search.trim().toLowerCase()
  const list = clientes
    .filter(cliente => !query || cliente.nombre.toLowerCase().includes(query))
    .filter(cliente => !onlyChanged || estaModificado(cliente))
    .sort((a, b) =>
      sort === SORT_HORA
        ? (toMinutes(a.inicio) ?? 0) - (toMinutes(b.inicio) ?? 0) ||
          (toMinutes(a.fin) ?? 0) - (toMinutes(b.fin) ?? 0) ||
          a.nombre.localeCompare(b.nombre, "es")
        : a.nombre.localeCompare(b.nombre, "es")
    )
    .map(cliente => ({
      ...cliente,
      selected: selected.includes(cliente.id),
      isDirty: estaModificado(cliente),
      invalid: esInvalido(cliente),
      duracion: duracion(cliente.inicio, cliente.fin),
    }))

  const comisiones = clientes.map(
    cliente => parseFloat(cliente.comision) || 0
  )
  const promedio = comisiones.length
    ? comisiones.reduce((acc, value) => acc + value, 0) / comisiones.length
    : 0

  const ventanas = {}
  clientes.forEach(cliente => {
    if (!cliente.inicio || !cliente.fin) return
    const key = `${cliente.inicio} – ${cliente.fin}`
    ventanas[key] = (ventanas[key] || 0) + 1
  })
  const ventanaTop =
    Object.keys(ventanas).sort((a, b) => ventanas[b] - ventanas[a])[0] || "—"

  const resumen = {
    total: clientes.length,
    promedio: `$${promedio.toFixed(2)}`,
    rango: comisiones.length
      ? `de $${Math.min(...comisiones).toFixed(2)} a $${Math.max(
          ...comisiones
        ).toFixed(2)}`
      : "—",
    ventanaTop,
    ventanaTopCount: `${ventanas[ventanaTop] || 0} clientes con esta ventana`,
    provDuracion: `${duracion(modelo?.prov?.start, modelo?.prov?.end)} abierto`,
  }

  const listadoLabel =
    list.length === clientes.length
      ? `${clientes.length} clientes`
      : `${list.length} de ${clientes.length} clientes`

  const marcarSucio = () => {
    clearTimeout(savedTimer.current)
    setIsSaved(false)
  }

  const onChangeProv = (campo, value) => {
    marcarSucio()
    setModelo(previo => ({ ...previo, prov: { ...previo.prov, [campo]: value } }))
  }

  const onChangeCliente = (id, cambios) => {
    marcarSucio()
    setModelo(previo => ({
      ...previo,
      clientes: previo.clientes.map(cliente =>
        cliente.id === id ? { ...cliente, ...cambios } : cliente
      ),
    }))
  }

  const onToggleSeleccion = id =>
    setSelected(previo =>
      previo.includes(id)
        ? previo.filter(item => item !== id)
        : [...previo, id]
    )

  const todosSeleccionados =
    list.length > 0 && list.every(cliente => selected.includes(cliente.id))

  const onToggleTodos = () =>
    setSelected(todosSeleccionados ? [] : list.map(cliente => cliente.id))

  const onClearSeleccion = () => setSelected([])

  const onChangeBulk = (campo, value) =>
    setBulk(previo => ({ ...previo, [campo]: value }))

  const onAplicarBulk = () => {
    marcarSucio()
    setModelo(previo => ({
      ...previo,
      clientes: previo.clientes.map(cliente => {
        if (!selected.includes(cliente.id)) return cliente
        return {
          ...cliente,
          inicio: bulk.inicio || cliente.inicio,
          fin: bulk.fin || cliente.fin,
          comision:
            bulk.comision !== ""
              ? (parseFloat(bulk.comision) || 0).toFixed(2)
              : cliente.comision,
        }
      }),
    }))
    setSelected([])
  }

  const onDescartar = () => {
    marcarSucio()
    setModelo(clonar(original))
    setSelected([])
  }

  const onGuardar = async () => {
    if (!dirtyCount || hayInvalidos || isSaving) return

    const data = []
    if (provModificada) {
      data.push({
        id: "providers",
        start: modelo.prov.start,
        end: modelo.prov.end,
        fee_per_unit: 0,
        isClient: false,
      })
    }
    clientesModificados.forEach(cliente => {
      data.push({
        id: cliente.id,
        start: cliente.inicio,
        end: cliente.fin,
        fee_per_unit: parseFloat(cliente.comision) || 0,
        isClient: true,
      })
    })

    setIsSaving(true)
    try {
      const response = await putDistributorConfigs({ data })
      const normalizado = normalizar(response)
      setModelo(normalizado)
      setOriginal(clonar(normalizado))
      setSelected([])
      setIsSaved(true)
      clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(
        () => setIsSaved(false),
        SAVED_FEEDBACK_MS
      )
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
  }

  return {
    state: {
      isLoading,
      modelo,
      list,
      resumen,
      listadoLabel,
      search,
      sort,
      onlyChanged,
      selected,
      bulk,
      todosSeleccionados,
      dirtyCount,
      hayInvalidos,
      isSaving,
      isSaved,
    },
    events: {
      setSearch,
      setSort,
      setOnlyChanged,
      onChangeProv,
      onChangeCliente,
      onToggleSeleccion,
      onToggleTodos,
      onClearSeleccion,
      onChangeBulk,
      onAplicarBulk,
      onDescartar,
      onGuardar,
    },
  }
}
